import type { FC, RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineTag,
  HiOutlineUser,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import {
  Button,
  InstantSearch,
  MultiSelectFilterDropdown,
  MultiSelectFilterOption,
  SingleSelectRadioFilterDropdown,
  SingleSelectFilterOption,
  ClearFiltersButton,
  CopyLinkButton,
} from "@components";

const FILTER_PANEL_CLASS_NAME =
  "absolute z-10 mt-1 w-60 bg-white rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600";
const FILTER_PANEL_STYLE: React.CSSProperties = {
  width: "240px",
  height: "auto",
  minHeight: "200px",
  maxHeight: "300px",
};

export interface AllOrdersFilterBarProps {
  hasSelectedOrder: boolean;
  splitIsOpen: boolean;
  customerSearchText: string;
  onCustomerSearch: (value: string) => void;

  statusDropdownRef: RefObject<HTMLDivElement>;
  statusDropdownOpen: boolean;
  onToggleStatusDropdown: (e: React.MouseEvent) => void;
  statusOptions: MultiSelectFilterOption[];
  onStatusOptionClick: (key: string, e: React.MouseEvent) => void;
  onClearStatus: () => void;
  statusFilterCount: number;

  driverDropdownRef: RefObject<HTMLDivElement>;
  driverDropdownOpen: boolean;
  onToggleDriverDropdown: (e: React.MouseEvent) => void;
  driverOptions: SingleSelectFilterOption[];
  driverFilter?: string;
  onDriverOptionClick: (key: string, e: React.MouseEvent) => void;
  onClearDriver: () => void;

  missingInfo: {
    visible: boolean;
    active: boolean;
    count: number;
    disabled: boolean;
    onToggle: () => void;
  };

  showActions: boolean;
  onClearFilters: () => void;
  onCopyShareableLink: () => void;
  onCreateNewOrder: () => void;
}

/** Filter bar (search + status/driver dropdowns + actions) for the All Orders page. */
export const AllOrdersFilterBar: FC<AllOrdersFilterBarProps> = ({
  hasSelectedOrder,
  splitIsOpen,
  customerSearchText,
  onCustomerSearch,
  statusDropdownRef,
  statusDropdownOpen,
  onToggleStatusDropdown,
  statusOptions,
  onStatusOptionClick,
  onClearStatus,
  statusFilterCount,
  driverDropdownRef,
  driverDropdownOpen,
  onToggleDriverDropdown,
  driverOptions,
  driverFilter,
  onDriverOptionClick,
  onClearDriver,
  missingInfo,
  showActions,
  onClearFilters,
  onCopyShareableLink,
  onCreateNewOrder,
}) => {
  const { t } = useTranslation(["common", "orders"]);

  return (
    <div
      className={`flex flex-col md:flex-row md:flex-wrap gap-2 mb-2 ${hasSelectedOrder ? "hidden lg:flex" : "flex"}`}
    >
      {/* Customer search */}
      <InstantSearch
        placeholder={t("common:search_customer")}
        value={customerSearchText}
        onChange={onCustomerSearch}
        className="min-w-0 md:min-w-[180px]"
      />

      {/* Filter controls share one row, including the missing-info toggle. */}
      <div className="flex items-start gap-2 md:gap-3 [&>div]:flex-1 md:[&>div]:flex-none">
        <MultiSelectFilterDropdown
          containerRef={statusDropdownRef}
          isOpen={statusDropdownOpen}
          onToggle={onToggleStatusDropdown}
          icon={<HiOutlineTag className="h-5 w-5" />}
          label={t("common:status")}
          options={statusOptions}
          onOptionClick={onStatusOptionClick}
          headerTitle={t("common:status")}
          selectedCount={statusFilterCount}
          onClear={onClearStatus}
          panelClassName={FILTER_PANEL_CLASS_NAME}
          panelStyle={FILTER_PANEL_STYLE}
        />

        {missingInfo.visible && (
          <button
            type="button"
            aria-pressed={missingInfo.active}
            title={t("orders:driverOrder.filterMissing", {
              count: missingInfo.count,
            })}
            disabled={missingInfo.disabled}
            onClick={missingInfo.onToggle}
            className={`flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border p-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50 ${
              missingInfo.active
                ? "border-brand bg-brand-muted text-brand"
                : "border-gray-300 bg-gray-50 text-gray-900 hover:border-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:border-brand"
            }`}
          >
            <HiOutlineExclamationCircle
              className="h-5 w-5"
              aria-hidden="true"
            />
            <span>{t("orders:driverOrder.filterMissingShort")}</span>
            <span className="tabular-nums">({missingInfo.count})</span>
          </button>
        )}

        <SingleSelectRadioFilterDropdown
          containerRef={driverDropdownRef}
          isOpen={driverDropdownOpen}
          onToggle={onToggleDriverDropdown}
          icon={<HiOutlineUser className="h-5 w-5" />}
          label={t("common:driver")}
          options={driverOptions}
          selectedKey={driverFilter}
          onOptionClick={onDriverOptionClick}
          headerTitle={t("common:drivers")}
          radioName="driverFilter"
          onClear={onClearDriver}
          panelClassName={FILTER_PANEL_CLASS_NAME}
          panelStyle={FILTER_PANEL_STYLE}
        />
      </div>

      {/* Action buttons row - Create new order, Clear filters, Share link */}
      <div className="flex gap-2 md:gap-3 mt-2 md:mt-0">
        {showActions && (
          <>
            <ClearFiltersButton
              onClick={onClearFilters}
              title={t("common:clear_filters")}
              className="flex-1 md:flex-auto"
            />
            <CopyLinkButton
              onClick={onCopyShareableLink}
              title={t("common:copy_link")}
              className="flex-1 md:flex-auto"
            />
          </>
        )}

        <Button
          variant="add"
          onClick={onCreateNewOrder}
          className={splitIsOpen ? "hidden lg:flex" : ""}
          size="md"
        >
          {t("orders:create.title")}
        </Button>
      </div>
    </div>
  );
};
